import { chordAt, chordParts, chordText, lyricLines } from '../lib/chords'
import { sectionLabel } from './ui'

/**
 * One chord, set the way a chart sets it.
 *
 * In the numbers view the extension is raised, because "5" plus "9" run
 * together is "59" — fifty-nine — and a player reading a chart at speed should
 * never have to work out whether that is a chord or a number. Nashville
 * notation superscripts extensions for exactly this reason. Letters don't need
 * it: "D9" cannot be misread, and raising it there would just look fussy.
 */
export function Chord({ ch, mode, songKey }) {
  const { root, suffix, bass } = chordParts(ch, { mode, key: songKey })
  if (!root) return null
  return (
    <span className="chd">
      {root}
      {suffix && (
        <span className={mode === 'numbers' ? 'chd-sup' : 'chd-suf'}>{suffix}</span>
      )}
      {bass && <span className="chd-bass">/{bass}</span>}
    </span>
  )
}

/**
 * Lyrics with chords sitting over the word they land on.
 *
 * One component for reading and for editing — `onPick` is what separates them.
 * Two components would drift, and the whole promise of the editor is that what
 * you place is what a player sees.
 *
 * The chord and its word are one inline-block, so the chord travels with the
 * word when the line wraps. Charts that position chords absolutely over a
 * monospace line break the moment the viewport is narrower than the longest
 * line — which on a phone, held by someone playing, is always.
 */
export default function ChordChart({ sections, chordsBySection, mode, songKey, onPick, active }) {
  return (
    <div className="chart">
      {sections.map((s) => {
        const chords = chordsBySection?.[s.id] ?? s.chords ?? []
        const lines = lyricLines(s.lyrics)
        const empty = lines.every((l) => l.words.length === 0)

        return (
          <section className="chart-sec" key={s.id}>
            <h3 className="chart-sec-label">{sectionLabel(s)}</h3>

            {empty ? (
              <p className="small">No words in this section yet.</p>
            ) : lines.map((line, li) => (
              line.words.length === 0
                ? <div className="chart-gap" key={li} aria-hidden="true" />
                : (
                  <p className="chart-line" key={li}>
                    {line.words.map((w, wi) => {
                      const ch = chordAt(chords, li, wi)
                      const isActive = active && active.l === li && active.w === wi &&
                        active.sectionId === s.id
                      const text = ch ? chordText(ch, { mode, key: songKey }) : ''
                      const Tag = onPick ? 'button' : 'span'
                      return (
                        <Tag
                          key={wi}
                          type={onPick ? 'button' : undefined}
                          className={`chart-w${ch ? ' has' : ''}${isActive ? ' active' : ''}`}
                          onClick={onPick ? () => onPick(s, li, wi, ch) : undefined}
                          aria-label={onPick
                            ? (ch ? `${text} on "${w}" — change` : `Add a chord on "${w}"`)
                            : undefined}
                        >
                          <span className="chart-ch">
                            {ch && <Chord ch={ch} mode={mode} songKey={songKey} />}
                          </span>
                          <span className="chart-word">{w}</span>
                        </Tag>
                      )
                    })}
                  </p>
                )
            ))}
          </section>
        )
      })}
    </div>
  )
}
